import React, { useState, useCallback } from 'react';
import { format } from 'date-fns';
import {
    ChevronDown,
    Calendar,
    User,
    Loader2,
    Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TaskStatus, TaskPriority, CustomField } from '@/lib/schemas';
import { statusConfig, priorityConfig, getStatusLabel, getPriorityLabel } from './task-table';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

function getInitials(name: string) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

interface QuickAddTaskFormData {
    title: string;
    status: TaskStatus;
    priority: TaskPriority;
    dueDate: string | null;
    assigneeIds: string[];
    customFieldValues?: Record<string, unknown>;
}

interface FilterDefaults {
    status?: TaskStatus;
    priority?: TaskPriority;
    assigneeId?: string;
    // t43 (R13): accepts the new per-type filter shape OR the legacy
    // option-id-list. The runtime consumer is responsible for
    // normalising to one or the other.
    customFields?: Record<string, unknown>;
}

interface QuickAddTaskPopoverProps {
    projectMembers: Array<{ userId: string; user: { id: string; name: string; avatarUrl: string | null } }>;
    onSubmit: (data: QuickAddTaskFormData, parentId?: string) => Promise<void>;
    parentId?: string;
    isSubtask?: boolean;
    buttonClassName?: string;
    defaultExpanded?: boolean;
    onCancel?: () => void;
    customFields?: CustomField[];
    filterDefaults?: FilterDefaults;
}

export function QuickAddTaskPopover({
    projectMembers,
    onSubmit,
    parentId,
    isSubtask = false,
    buttonClassName,
    defaultExpanded = false,
    onCancel,
    customFields = [],
    filterDefaults,
}: QuickAddTaskPopoverProps) {
    const { t } = useI18n();
    const [isOpen, setIsOpen] = useState(defaultExpanded);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Build initial custom field values from filter defaults
    const getInitialCustomFieldValues = () => {
        if (!filterDefaults?.customFields) return {};
        const values: Record<string, unknown> = {};
        for (const [fieldId, raw] of Object.entries(filterDefaults.customFields)) {
            // t43 (R13): only the legacy option-id-list shape can seed
            // initial values; the new per-type filter shape has its own
            // semantics and is applied server-side at query time.
            const optionIds = Array.isArray(raw) ? (raw as string[]) : null;
            if (optionIds && optionIds.length > 0) {
                const field = customFields.find(f => f.id === fieldId);
                if (field?.type === 'MULTI_SELECT') {
                    values[fieldId] = optionIds;
                } else if (field?.type === 'SELECT' && optionIds.length === 1) {
                    values[fieldId] = optionIds[0];
                }
            }
        }
        return values;
    };

    const [formData, setFormData] = useState<QuickAddTaskFormData>({
        title: '',
        status: filterDefaults?.status || 'TODO',
        priority: filterDefaults?.priority || 'NONE',
        dueDate: null,
        assigneeIds: filterDefaults?.assigneeId ? [filterDefaults.assigneeId] : [],
        customFieldValues: getInitialCustomFieldValues(),
    });

    const resetForm = useCallback(() => {
        setFormData({
            title: '',
            status: filterDefaults?.status || 'TODO',
            priority: filterDefaults?.priority || 'NONE',
            dueDate: null,
            assigneeIds: filterDefaults?.assigneeId ? [filterDefaults.assigneeId] : [],
            customFieldValues: getInitialCustomFieldValues(),
        });
    }, [filterDefaults, customFields]);

    const handleSubmit = async () => {
        if (!formData.title.trim()) return;

        setIsSubmitting(true);
        try {
            await onSubmit(formData, parentId);
            resetForm();
            if (!defaultExpanded) {
                setIsOpen(false);
            }
        } catch (error) {
            console.error('Failed to create task:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        } else if (e.key === 'Escape') {
            if (defaultExpanded) {
                onCancel?.();
            } else {
                setIsOpen(false);
            }
            resetForm();
        }
    };

    const handleCancel = () => {
        resetForm();
        if (defaultExpanded) {
            onCancel?.();
        } else {
            setIsOpen(false);
        }
    };

    const toggleAssignee = (userId: string) => {
        setFormData((prev) => ({
            ...prev,
            assigneeIds: prev.assigneeIds.includes(userId)
                ? prev.assigneeIds.filter((id) => id !== userId)
                : [...prev.assigneeIds, userId],
        }));
    };

    const updateCustomFieldValue = (fieldId: string, value: unknown) => {
        setFormData((prev) => ({
            ...prev,
            customFieldValues: {
                ...prev.customFieldValues,
                [fieldId]: value,
            },
        }));
    };

    const statusConf = statusConfig[formData.status];
    const priorityConf = priorityConfig[formData.priority];
    const selectedAssignees = projectMembers.filter((m) => formData.assigneeIds.includes(m.userId));

    const formContent = (
        <div className="space-y-3">
            {/* Title input */}
            <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                onKeyDown={handleKeyDown}
                placeholder={isSubtask ? t('tasks.taskName') + '...' : t('tasks.taskName') + '...'}
                className="h-9"
                autoFocus
                disabled={isSubmitting}
            />

            {/* Quick fields row */}
            <div className="flex flex-wrap gap-2 items-center">
                {/* Status */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 gap-1">
                            <Badge variant={statusConf.variant} className="text-xs">
                                {getStatusLabel(formData.status, t)}
                            </Badge>
                            <ChevronDown className="h-3 w-3 text-foreground-muted" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        {Object.entries(statusConfig).map(([key, config]) => (
                            <DropdownMenuItem
                                key={key}
                                onClick={() => setFormData({ ...formData, status: key as TaskStatus })}
                            >
                                <Badge variant={config.variant} className="mr-2">
                                    {getStatusLabel(key as TaskStatus, t)}
                                </Badge>
                                {formData.status === key && <span className="ml-auto">✓</span>}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Priority */}
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 gap-1">
                            <Badge variant={priorityConf.variant} className="text-xs">
                                {getPriorityLabel(formData.priority, t)}
                            </Badge>
                            <ChevronDown className="h-3 w-3 text-foreground-muted" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        {Object.entries(priorityConfig).map(([key, config]) => (
                            <DropdownMenuItem
                                key={key}
                                onClick={() => setFormData({ ...formData, priority: key as TaskPriority })}
                            >
                                <Badge variant={config.variant} className="mr-2">
                                    {getPriorityLabel(key as TaskPriority, t)}
                                </Badge>
                                {formData.priority === key && <span className="ml-auto">✓</span>}
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Due Date */}
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 gap-1">
                            <Calendar className="h-3.5 w-3.5 text-foreground-muted" />
                            <span className="text-xs">
                                {formData.dueDate
                                    ? format(new Date(formData.dueDate), 'MMM d')
                                    : t('tasks.dueDate')}
                            </span>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3" align="start">
                        <div className="space-y-2">
                            <Input
                                type="date"
                                value={formData.dueDate ? formData.dueDate.split('T')[0] : ''}
                                onChange={(e) => {
                                    const newDate = e.target.value
                                        ? new Date(e.target.value + 'T00:00:00.000Z').toISOString()
                                        : null;
                                    setFormData({ ...formData, dueDate: newDate });
                                }}
                                className="w-full"
                            />
                            {formData.dueDate && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="w-full text-foreground-muted"
                                    onClick={() => setFormData({ ...formData, dueDate: null })}
                                >
                                    {t('tasks.clearDate')}
                                </Button>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>

                {/* Assignees */}
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 gap-1">
                            {selectedAssignees.length > 0 ? (
                                <div className="flex -space-x-1">
                                    {selectedAssignees.slice(0, 2).map((member) => (
                                        <Avatar key={member.userId} className="h-5 w-5 border border-background">
                                            <AvatarImage src={member.user.avatarUrl || undefined} />
                                            <AvatarFallback className="text-[8px]">
                                                {getInitials(member.user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                    ))}
                                    {selectedAssignees.length > 2 && (
                                        <span className="text-xs text-foreground-muted ml-1">
                                            +{selectedAssignees.length - 2}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <User className="h-3.5 w-3.5 text-foreground-muted" />
                                    <span className="text-xs">{t('tasks.assignTo')}</span>
                                </>
                            )}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="start">
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                            <p className="text-xs font-medium text-foreground-muted px-2 py-1">
                                {t('tasks.selectAssignees')}
                            </p>
                            {projectMembers.map((member) => (
                                <label
                                    key={member.userId}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                                >
                                    <Checkbox
                                        checked={formData.assigneeIds.includes(member.userId)}
                                        onCheckedChange={() => toggleAssignee(member.userId)}
                                    />
                                    <Avatar className="h-6 w-6">
                                        <AvatarImage src={member.user.avatarUrl || undefined} />
                                        <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">
                                            {getInitials(member.user.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm truncate">{member.user.name}</span>
                                </label>
                            ))}
                            {projectMembers.length === 0 && (
                                <p className="text-sm text-foreground-muted text-center py-2">
                                    {t('common.none')}
                                </p>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>

                {/* Custom Fields */}
                {customFields.map((field) => {
                    const value = formData.customFieldValues?.[field.id];

                    // SELECT field
                    if (field.type === 'SELECT') {
                        const selectedOption = field.options.find(opt => opt.id === value);
                        return (
                            <DropdownMenu key={field.id}>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 gap-1">
                                        {selectedOption ? (
                                            <Badge
                                                variant="secondary"
                                                className="text-xs"
                                                style={selectedOption.color ? {
                                                    backgroundColor: `${selectedOption.color}20`,
                                                    borderColor: selectedOption.color,
                                                    color: selectedOption.color
                                                } : undefined}
                                            >
                                                {selectedOption.label}
                                            </Badge>
                                        ) : (
                                            <span className="text-xs text-foreground-muted">{field.name}</span>
                                        )}
                                        <ChevronDown className="h-3 w-3 text-foreground-muted" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuItem onClick={() => updateCustomFieldValue(field.id, null)}>
                                        <span className="text-foreground-muted">None</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {field.options.map(opt => (
                                        <DropdownMenuItem
                                            key={opt.id}
                                            onClick={() => updateCustomFieldValue(field.id, opt.id)}
                                        >
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                                style={{ backgroundColor: opt.color || '#6366f1' }}
                                            >
                                                {opt.label}
                                            </span>
                                            {value === opt.id && <span className="ml-auto">✓</span>}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        );
                    }

                    // MULTI_SELECT field
                    if (field.type === 'MULTI_SELECT') {
                        const selectedIds = Array.isArray(value) ? value : [];
                        const selectedOptions = field.options.filter(opt => selectedIds.includes(opt.id));
                        return (
                            <Popover key={field.id}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 gap-1 max-w-[150px]">
                                        {selectedOptions.length > 0 ? (
                                            <div className="flex gap-1 overflow-hidden">
                                                {selectedOptions.slice(0, 2).map(opt => (
                                                    <Badge
                                                        key={opt.id}
                                                        variant="secondary"
                                                        className="text-xs"
                                                        style={opt.color ? {
                                                            backgroundColor: `${opt.color}20`,
                                                            borderColor: opt.color,
                                                            color: opt.color
                                                        } : undefined}
                                                    >
                                                        {opt.label}
                                                    </Badge>
                                                ))}
                                                {selectedOptions.length > 2 && (
                                                    <span className="text-xs text-foreground-muted">
                                                        +{selectedOptions.length - 2}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-foreground-muted">{field.name}</span>
                                        )}
                                        <ChevronDown className="h-3 w-3 text-foreground-muted" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 p-2" align="start">
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                        {field.options.map(opt => (
                                            <label
                                                key={opt.id}
                                                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                                            >
                                                <Checkbox
                                                    checked={selectedIds.includes(opt.id)}
                                                    onCheckedChange={(checked) => {
                                                        const newValue = checked
                                                            ? [...selectedIds, opt.id]
                                                            : selectedIds.filter(id => id !== opt.id);
                                                        updateCustomFieldValue(field.id, newValue);
                                                    }}
                                                />
                                                <span
                                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                                    style={{ backgroundColor: opt.color || '#6366f1' }}
                                                >
                                                    {opt.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </PopoverContent>
                            </Popover>
                        );
                    }

                    // CHECKBOX field
                    if (field.type === 'CHECKBOX') {
                        return (
                            <label
                                key={field.id}
                                className="flex items-center gap-2 h-8 px-2 border rounded-md cursor-pointer hover:bg-surface-hover"
                            >
                                <Checkbox
                                    checked={value === true}
                                    onCheckedChange={(checked) => updateCustomFieldValue(field.id, checked)}
                                />
                                <span className="text-xs">{field.name}</span>
                            </label>
                        );
                    }

                    // PERSON field
                    if (field.type === 'PERSON') {
                        const selectedMember = projectMembers.find(m => m.userId === value);
                        return (
                            <DropdownMenu key={field.id}>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 gap-1">
                                        {selectedMember ? (
                                            <>
                                                <Avatar className="h-5 w-5">
                                                    <AvatarImage src={selectedMember.user.avatarUrl || undefined} />
                                                    <AvatarFallback className="text-[8px]">
                                                        {getInitials(selectedMember.user.name)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="text-xs truncate max-w-[60px]">{selectedMember.user.name}</span>
                                            </>
                                        ) : (
                                            <span className="text-xs text-foreground-muted">{field.name}</span>
                                        )}
                                        <ChevronDown className="h-3 w-3 text-foreground-muted" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuItem onClick={() => updateCustomFieldValue(field.id, null)}>
                                        <span className="text-foreground-muted">None</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {projectMembers.map(member => (
                                        <DropdownMenuItem
                                            key={member.userId}
                                            onClick={() => updateCustomFieldValue(field.id, member.userId)}
                                        >
                                            <Avatar className="h-6 w-6 mr-2">
                                                <AvatarImage src={member.user.avatarUrl || undefined} />
                                                <AvatarFallback className="text-xs">
                                                    {getInitials(member.user.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            {member.user.name}
                                            {value === member.userId && <span className="ml-auto">✓</span>}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        );
                    }

                    // For TEXT, NUMBER, DATE fields - show a compact input popover
                    if (field.type === 'TEXT' || field.type === 'NUMBER' || field.type === 'DATE' || field.type === 'URL' || field.type === 'EMAIL') {
                        return (
                            <Popover key={field.id}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-8 gap-1">
                                        <span className="text-xs text-foreground-muted">
                                            {value ? String(value).slice(0, 10) + (String(value).length > 10 ? '...' : '') : field.name}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-56 p-3" align="start">
                                    <div className="space-y-2">
                                        <label className="text-xs font-medium text-foreground-muted">{field.name}</label>
                                        <Input
                                            type={field.type === 'NUMBER' ? 'number' : field.type === 'DATE' ? 'date' : field.type === 'EMAIL' ? 'email' : field.type === 'URL' ? 'url' : 'text'}
                                            value={field.type === 'DATE' && value ? String(value).split('T')[0] : (value as string) || ''}
                                            onChange={(e) => {
                                                let newValue: unknown = e.target.value;
                                                if (field.type === 'NUMBER') {
                                                    newValue = e.target.value ? parseFloat(e.target.value) : null;
                                                } else if (field.type === 'DATE') {
                                                    newValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                                                }
                                                updateCustomFieldValue(field.id, newValue || null);
                                            }}
                                            className="h-8"
                                            placeholder={`Enter ${field.name.toLowerCase()}...`}
                                        />
                                    </div>
                                </PopoverContent>
                            </Popover>
                        );
                    }

                    return null;
                })}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={!formData.title.trim() || isSubmitting}
                >
                    {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        isSubtask ? t('tasks.addSubtask') : t('tasks.addTask')
                    )}
                </Button>
            </div>
        </div>
    );

    // If defaultExpanded, render inline without popover
    if (defaultExpanded) {
        return formContent;
    }

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <button
                    className={cn(
                        "flex items-center gap-2 text-foreground-muted hover:text-foreground transition-colors text-sm py-2",
                        buttonClassName
                    )}
                >
                    <Plus className="h-4 w-4" />
                    <span>{isSubtask ? t('tasks.addSubtask') : t('tasks.addTask')}</span>
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[450px] p-4" align="start">
                {formContent}
            </PopoverContent>
        </Popover>
    );
}
